import React, { useEffect, useId, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthContext.jsx';
import { useDeal } from '../deals/DealContext.jsx';
import { withDealQuery } from '../deals/registry.js';

/**
 * Header deal-mark dropdown — switch projects without a full-page-only flow.
 */
export default function ProjectSwitcher({
  modeMark = null,
  onOpenAllProjects,
  onCreateProject,
  currentPath = 'overview',
}) {
  const { deals, deal, dealId, selectDeal } = useDeal();
  const { isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const menuId = useId();
  const displayMark = modeMark || deal?.mark || 'D2D';
  const locked = Boolean(modeMark); // CRM / ABS mode marks are not deal switchers

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const switchTo = (id) => {
    const pathRaw = String(currentPath || 'overview').replace(/^#/, '');
    const path = pathRaw === 'projects' || pathRaw === 'deals' ? 'analysis' : pathRaw === 'verdict' ? 'analysis' : pathRaw || 'analysis';
    selectDeal(id);
    if (typeof window !== 'undefined') {
      window.location.hash = withDealQuery(path, id);
    }
    setOpen(false);
  };

  if (locked) {
    return (
      <div className="header-mark" aria-hidden="true">
        {displayMark}
      </div>
    );
  }

  return (
    <div className={`project-switcher ${open ? 'open' : ''}`} ref={rootRef}>
      <button
        type="button"
        className="header-mark project-switcher-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        title="Switch project"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="project-switcher-mark">{displayMark}</span>
        <span className="project-switcher-caret" aria-hidden="true">
          ▾
        </span>
      </button>
      {open ? (
        <ul id={menuId} className="project-switcher-menu" role="menu" aria-label="Projects">
          {deals.map((d) => (
            <li key={d.dealId} role="none">
              <button
                type="button"
                role="menuitem"
                className={`project-switcher-item ${d.dealId === dealId ? 'active' : ''}`}
                onClick={() => switchTo(d.dealId)}
              >
                <span className="project-switcher-item-mark">{d.mark}</span>
                <span className="project-switcher-item-copy">
                  <span className="project-switcher-item-label">
                    {d.label}
                    {d.draft ? ' (draft)' : ''}
                  </span>
                  <span className="project-switcher-item-sub">{d.subtitle}</span>
                </span>
              </button>
            </li>
          ))}
          <li role="none" className="project-switcher-sep" />
          <li role="none">
            <button
              type="button"
              role="menuitem"
              className="project-switcher-item project-switcher-item-action"
              onClick={() => {
                setOpen(false);
                onOpenAllProjects?.();
              }}
            >
              All projects…
            </button>
          </li>
          {isAdmin ? (
            <li role="none">
              <button
                type="button"
                role="menuitem"
                className="project-switcher-item project-switcher-item-action"
                onClick={() => {
                  setOpen(false);
                  onCreateProject?.();
                }}
              >
                Create new project
              </button>
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}
